import {
  clearGuestEncryptionSecret,
  decryptChatbotPayload,
  encryptChatbotPayload,
  invalidateChatbotEncryptionKeyCache,
  resolveChatbotEncryptionSecret,
} from './chatbotConversationCrypto';

const STORAGE_KEY = 'quickvee_chatbot_conversations';

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    return true;
  } catch {
    return false;
  }
};

export const normalizeChatbotEmailId = (emailId) => {
  // Guests and missing values must always be "" — never null/undefined.
  if (emailId == null) return '';
  return String(emailId).trim();
};

export const normalizeChatbotMerchantId = (merchantId) =>
  String(merchantId || '').trim();

export const normalizeChatbotPageScope = (pageScope) =>
  String(pageScope || '')
    .trim()
    .toLowerCase();

/**
 * Conversation identity = merchant_id + email_id + page_scope
 * Each side-menu page (products, stock-on-hand, …) keeps its own thread.
 * Examples:
 *   VIV102927MA_userA@example.com_inventory-new-products
 *   VIV102927MA__purchase-order    (guest + page)
 *   VIV102927MA_userA@example.com  (legacy / no page scope)
 */
export const buildConversationIdentityKey = (
  merchantId,
  emailId,
  pageScope,
) => {
  const merchant = normalizeChatbotMerchantId(merchantId);
  if (!merchant) return '';
  const email = normalizeChatbotEmailId(emailId);
  const scope = normalizeChatbotPageScope(pageScope);
  return scope ? `${merchant}_${email}_${scope}` : `${merchant}_${email}`;
};

const isPlainConversationRecord = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (Array.isArray(value.messages) ||
    value.threadId !== undefined ||
    value.merchantId !== undefined) &&
  value.v !== 2;

const isEncryptedConversationRecord = (value) =>
  value &&
  typeof value === 'object' &&
  value.v === 2 &&
  value.alg === 'AES-GCM' &&
  typeof value.data === 'string' &&
  typeof value.iv === 'string';

const resolveRawRecord = (store, merchant, email, pageScope) => {
  const flatKey = buildConversationIdentityKey(merchant, email, pageScope);
  const flat = store[flatKey];
  if (isEncryptedConversationRecord(flat) || isPlainConversationRecord(flat)) {
    return { record: flat, flatKey, legacyNested: false };
  }

  // Page-scoped keys only — do not fall back to shared merchant+email chats.
  if (normalizeChatbotPageScope(pageScope)) {
    return { record: null, flatKey, legacyNested: false };
  }

  const nested = store[merchant];
  if (
    nested &&
    typeof nested === 'object' &&
    !isEncryptedConversationRecord(nested) &&
    !isPlainConversationRecord(nested)
  ) {
    const legacyRecord = nested[email];
    if (
      isEncryptedConversationRecord(legacyRecord) ||
      isPlainConversationRecord(legacyRecord)
    ) {
      return { record: legacyRecord, flatKey, legacyNested: true };
    }
  }

  return { record: null, flatKey, legacyNested: false };
};

const sanitizeMessages = (messages) =>
  Array.isArray(messages)
    ? messages
        .filter((msg) => msg && !msg.pending)
        .map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
        }))
    : [];

const removeLegacyNested = (store, merchant, email) => {
  if (store[merchant] && typeof store[merchant] === 'object') {
    if (
      !isEncryptedConversationRecord(store[merchant]) &&
      !isPlainConversationRecord(store[merchant])
    ) {
      delete store[merchant][email];
      if (Object.keys(store[merchant]).length === 0) {
        delete store[merchant];
      }
    }
  }
};

/**
 * Load + decrypt conversation for the *current* merchant_id + email_id + page.
 * Does not trust stored identity for authorization — caller must pass live scope.
 * Auth users without a valid login_token: do not decrypt/restore.
 */
const readConversation = async (merchantId, emailId, pageScope = '') => {
  const merchant = normalizeChatbotMerchantId(merchantId);
  const email = normalizeChatbotEmailId(emailId);
  const scope = normalizeChatbotPageScope(pageScope);
  if (!merchant) return null;

  const secret = resolveChatbotEncryptionSecret(email);
  // Authenticated scope without token: never decrypt/restore.
  if (!secret) {
    return null;
  }

  const store = readStore();
  const { record, flatKey, legacyNested } = resolveRawRecord(
    store,
    merchant,
    email,
    scope,
  );
  if (!record) return null;

  // Migrate legacy plaintext → encrypted under current secret, then return.
  if (isPlainConversationRecord(record)) {
    // Never restore plaintext auth chats without a token (already gated above).
    const payload = {
      merchantId: merchant,
      emailId: email,
      pageScope: scope,
      threadId: record.threadId || null,
      messages: sanitizeMessages(record.messages),
      updatedAt: record.updatedAt || new Date().toISOString(),
    };

    try {
      const encrypted = await encryptChatbotPayload(
        secret,
        merchant,
        email,
        payload,
      );
      store[flatKey] = {
        ...encrypted,
        updatedAt: payload.updatedAt,
      };
      if (legacyNested) {
        removeLegacyNested(store, merchant, email);
      }
      writeStore(store);
    } catch {
      // If migration fails, drop plaintext so messages are never left readable.
      delete store[flatKey];
      if (legacyNested) {
        removeLegacyNested(store, merchant, email);
      }
      writeStore(store);
      return null;
    }

    return {
      merchantId: merchant,
      emailId: email,
      pageScope: scope,
      threadId: payload.threadId,
      messages: payload.messages,
      updatedAt: payload.updatedAt,
      identityKey: flatKey,
    };
  }

  const decrypted = await decryptChatbotPayload(
    secret,
    merchant,
    email,
    record,
  );
  if (!decrypted) {
    // Wrong/rotated token or tampered data — do not restore.
    return null;
  }

  // Verify decrypted identity matches the live (requested) scope.
  const decryptedMerchant = normalizeChatbotMerchantId(decrypted.merchantId);
  const decryptedEmail = normalizeChatbotEmailId(decrypted.emailId);
  const decryptedScope = normalizeChatbotPageScope(decrypted.pageScope);
  if (
    decryptedMerchant !== merchant ||
    decryptedEmail !== email ||
    (scope && decryptedScope && decryptedScope !== scope)
  ) {
    return null;
  }

  if (legacyNested) {
    store[flatKey] = record;
    removeLegacyNested(store, merchant, email);
    writeStore(store);
  }

  return {
    merchantId: merchant,
    emailId: email,
    pageScope: scope,
    threadId: decrypted.threadId || null,
    messages: sanitizeMessages(decrypted.messages),
    updatedAt: decrypted.updatedAt || record.updatedAt || null,
    identityKey: flatKey,
  };
};

// Reads and saves await crypto, so without ordering an older snapshot can land
// after a newer one, or a load can miss a write still in flight. Every
// storage operation runs through this chain.
let pendingTask = Promise.resolve();

const enqueueStorageTask = (task) => {
  const run = pendingTask.then(task, task);
  pendingTask = run.catch(() => undefined);
  return run;
};

export const loadChatbotConversation = (merchantId, emailId, pageScope = '') =>
  enqueueStorageTask(() => readConversation(merchantId, emailId, pageScope));

const writeConversation = async (
  merchantId,
  emailId,
  { messages = [], threadId = null, pageScope = '' } = {},
) => {
  const merchant = normalizeChatbotMerchantId(merchantId);
  const email = normalizeChatbotEmailId(emailId);
  const scope = normalizeChatbotPageScope(pageScope);
  const identityKey = buildConversationIdentityKey(merchant, email, scope);
  if (!identityKey) return false;

  const secret = resolveChatbotEncryptionSecret(email);
  if (!secret) {
    // Authenticated user without token — refuse to persist (never plaintext).
    return false;
  }

  const updatedAt = new Date().toISOString();
  const payload = {
    merchantId: merchant,
    emailId: email,
    pageScope: scope,
    threadId: threadId || null,
    messages: sanitizeMessages(messages),
    updatedAt,
  };

  let encrypted;
  try {
    encrypted = await encryptChatbotPayload(secret, merchant, email, payload);
  } catch {
    return false;
  }

  const store = readStore();
  removeLegacyNested(store, merchant, email);

  // Store only ciphertext envelope — no plaintext messages.
  store[identityKey] = {
    ...encrypted,
    updatedAt,
  };

  return writeStore(store);
};

/**
 * Encrypt and save conversation for merchant_id + email_id + page_scope.
 * Auth users without login_token: skip save (never write plaintext).
 */
export const saveChatbotConversation = (
  merchantId,
  emailId,
  { messages = [], threadId = null, pageScope = '' } = {},
) => {
  // Copy now: callers may pass Redux/Immer drafts that are revoked before the
  // queued write runs.
  const snapshot = { messages: sanitizeMessages(messages), threadId, pageScope };
  return enqueueStorageTask(() =>
    writeConversation(merchantId, emailId, snapshot),
  );
};

/**
 * Append messages to a stored conversation that is not the one on screen,
 * e.g. a reply that arrives after the user moved to another page.
 */
export const appendChatbotConversationMessages = (
  merchantId,
  emailId,
  { pageScope = '', messages = [], threadId = null } = {},
) => {
  const incoming = sanitizeMessages(messages);
  return enqueueStorageTask(async () => {
    const saved = await readConversation(merchantId, emailId, pageScope);
    const known = new Set((saved?.messages || []).map((msg) => msg.id));
    return writeConversation(merchantId, emailId, {
      pageScope,
      threadId: threadId || saved?.threadId || null,
      messages: [
        ...(saved?.messages || []),
        ...incoming.filter((msg) => !known.has(msg.id)),
      ],
    });
  });
};

export const clearChatbotConversation = (
  merchantId,
  emailId,
  pageScope = '',
) =>
  enqueueStorageTask(() => {
    const merchant = normalizeChatbotMerchantId(merchantId);
    const email = normalizeChatbotEmailId(emailId);
    const scope = normalizeChatbotPageScope(pageScope);
    const identityKey = buildConversationIdentityKey(merchant, email, scope);
    if (!identityKey) return false;

    const store = readStore();
    delete store[identityKey];
    if (!scope) {
      removeLegacyNested(store, merchant, email);
    }
    return writeStore(store);
  });

/** Remove every saved chatbot conversation (all merchants / users). */
export const clearAllChatbotConversations = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    clearGuestEncryptionSecret();
    invalidateChatbotEncryptionKeyCache();
    return true;
  } catch {
    return false;
  }
};
